/**
 * Masters API endpoints (workflow statuses, priorities, task types)
 */
import axiosClient from './axiosClient';
import { API_BASE_URL, TOKEN_KEY } from '@/utils/constants';

export interface WorkflowStatus {
  id: string;
  name: string;
  color?: string;
  category?: string;
  is_initial?: boolean;
  is_final?: boolean;
  order: number;
  project_id?: string;
}

export interface TaskType {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  is_system?: boolean;
  is_active?: boolean;
}

export interface Priority {
  id: string;
  key: string;
  name: string;
  description?: string;
  color?: string;
  order: number;
  is_active?: boolean;
  project_id?: string;
}

export interface IworkFlowDetails {
  project_id: string
  statuses: IworkFlowListDetails[]
}

export interface IworkFlowListDetails {
  id: string
  name: string
  category: string
  order: number
  is_first: boolean,
  is_initial?: boolean;
  is_last: boolean
  color: string
}

export interface IreorderWorkFlowStatues{
  projectId:string
  draggedWorflowStatusId:string
  prevWorkFlowStatusId:string|null
  nextWorkFlowStatusId:string|null
}

export interface IreorderWorkFlowResponse {
  id: string
  name: string
  new_order: number
  message: string
  statuses: IreoderWorkflowStatues[]
}

export interface IreoderWorkflowStatues {
  id: string
  name: string
  category: string
  order: number
  is_first: boolean
  is_last: boolean
  color: string
  description: any
  is_initial: boolean
  project_id: any
}


export interface IupdateWorkflowStatusPayload {
  new_workflow_status_name: string;
  workflow_statues_id: string;
}

export interface IupdateWorkflowStatusResponse {
  message: string;
  status_id: string;
  updated_name: string;
}


// Master data (workflow statuses, priorities, task types) is global, immutable
// for the duration of a session, and used by ~17 components. Without caching
// every page navigation re-fetched all three (× StrictMode = 6 calls). With a
// long TTL + in-flight dedup, the entire app makes 3 calls per page reload at
// most, and 0 calls on subsequent navigations within the TTL.
const MASTERS_TTL_MS = 10 * 60_000;
let workflowStatusesCache: { ts: number; data: WorkflowStatus[] } | null = null;
let workflowStatusesInFlight: Promise<WorkflowStatus[]> | null = null;
let taskTypesCache: { ts: number; data: TaskType[] } | null = null;
let taskTypesInFlight: Promise<TaskType[]> | null = null;
let prioritiesCache: { ts: number; data: Priority[] } | null = null;
let prioritiesInFlight: Promise<Priority[]> | null = null;
const isFresh = <T>(c: { ts: number; data: T } | null) =>
  c !== null && Date.now() - c.ts < MASTERS_TTL_MS;

export const mastersAPI = {
  getWorkflowStatuses: async (): Promise<WorkflowStatus[]> => {
    if (isFresh(workflowStatusesCache)) return workflowStatusesCache!.data;
    if (workflowStatusesInFlight) return workflowStatusesInFlight;
    workflowStatusesInFlight = (async () => {
      try {
        try {
          const response = await axiosClient.get<WorkflowStatus[]>('/workflow-statuses');
          workflowStatusesCache = { ts: Date.now(), data: response.data };
          return response.data;
        } catch (err: any) {
          // Fallback: bypass axios interceptors to avoid global 401 redirect
          try {
            const token = localStorage.getItem(TOKEN_KEY);
            const res = await fetch(`${API_BASE_URL}/workflow-statuses`, {
              headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              credentials: 'include',
            });
            if (!res.ok) return [];
            const data = await res.json();
            workflowStatusesCache = { ts: Date.now(), data };
            return data;
          } catch {
            return [];
          }
        }
      } finally {
        workflowStatusesInFlight = null;
      }
    })();
    return workflowStatusesInFlight;
  },

  getTaskTypes: async (): Promise<TaskType[]> => {
    if (isFresh(taskTypesCache)) return taskTypesCache!.data;
    if (taskTypesInFlight) return taskTypesInFlight;
    taskTypesInFlight = (async () => {
      try {
        try {
          const response = await axiosClient.get<TaskType[]>('/task-types');
          taskTypesCache = { ts: Date.now(), data: response.data };
          return response.data;
        } catch (err: any) {
          try {
            const token = localStorage.getItem(TOKEN_KEY);
            const res = await fetch(`${API_BASE_URL}/task-types`, {
              headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              credentials: 'include',
            });
            if (!res.ok) return [];
            const data = await res.json();
            taskTypesCache = { ts: Date.now(), data };
            return data;
          } catch {
            return [];
          }
        }
      } finally {
        taskTypesInFlight = null;
      }
    })();
    return taskTypesInFlight;
  },

  getPriorities: async (): Promise<Priority[]> => {
    if (isFresh(prioritiesCache)) return prioritiesCache!.data;
    if (prioritiesInFlight) return prioritiesInFlight;
    prioritiesInFlight = (async () => {
      try {
        try {
          const response = await axiosClient.get<Priority[]>('/priorities');
          prioritiesCache = { ts: Date.now(), data: response.data };
          return response.data;
        } catch (err: any) {
          try {
            const token = localStorage.getItem(TOKEN_KEY);
            const res = await fetch(`${API_BASE_URL}/priorities`, {
              headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              credentials: 'include',
            });
            if (!res.ok) return [];
            const data = await res.json();
            prioritiesCache = { ts: Date.now(), data };
            return data;
          } catch {
            return [];
          }
        }
      } finally {
        prioritiesInFlight = null;
      }
    })();
    return prioritiesInFlight;
  },

  /** Invalidate master-data caches. Call after admin mutations to these tables. */
  invalidateMastersCache: () => {
    workflowStatusesCache = null;
    taskTypesCache = null;
    prioritiesCache = null;
  },

  getWorkflowStatusesList: async (projectId:string): Promise<IworkFlowDetails> => {
    try {
      const response = await axiosClient.get<IworkFlowDetails>(`/workflow-statuses/project/${projectId}`);
      return response.data;
    } catch (err: any) {
      // Fallback: bypass axios interceptors to avoid global 401 redirect
      try {
        const token = localStorage.getItem(TOKEN_KEY);
        const url = `${API_BASE_URL}/workflow-statuses/project/`;
        const res = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: 'include',
        });
        if (!res.ok) {
          return {project_id:projectId,statuses:[] };
        }
        const data = await res.json();
        return data;
      } catch (fetchErr) {
        return {project_id:projectId,statuses:[] };
      }
    }
  },
  reOrderWorkStatues: async (payloadData:IreorderWorkFlowStatues): Promise<IreorderWorkFlowResponse> => {
    try {
      const response = await axiosClient.post<IreorderWorkFlowResponse>(`/workflow-statuses/${payloadData.projectId}/statuses/${payloadData.draggedWorflowStatusId}/reorder`,{
        prev_status_id:payloadData.prevWorkFlowStatusId,
        next_status_id:payloadData.nextWorkFlowStatusId
      });
      return response.data;
    } catch (err: any) {
      // Fallback: bypass axios interceptors to avoid global 401 redirect
      try {
        const token = localStorage.getItem(TOKEN_KEY);
        const url = `${API_BASE_URL}/workflow-statuses/project/`;
        const res = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: 'include',
        });
        if (!res.ok) {
          return {id: '',
            name: '',
            new_order:0 ,
            message: '',
            statuses: []
          };
        }
        const data = await res.json();
        return data;
      } catch (fetchErr) {
        return  {id: '',
          name: '',
          new_order:0 ,
          message: '',
          statuses: []
        };
      }
    }
  },
  createWorkflowStatus: async (projectId: string, data: { name: string; color: string; category: string }): Promise<WorkflowStatus> => {
    try {
      const response = await axiosClient.post<WorkflowStatus>(`/projects/${projectId}/workflow-statuses`, data);
      return response.data;
    } catch (err: any) {
      // Fallback
      const token = localStorage.getItem(TOKEN_KEY);
      const url = `${API_BASE_URL}/projects/${projectId}/workflow-statuses`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
        credentials: 'include',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.message || data?.detail || `Failed to create workflow status (${res.status})`);
      }
      return await res.json();
    }
  },

  updateWorkflowStatusName: async (projectId: string, data: IupdateWorkflowStatusPayload): Promise<IupdateWorkflowStatusResponse> => {
    try {
      const response = await axiosClient.post<IupdateWorkflowStatusResponse>(`/workflow-statuses/${projectId}/workflow-statuses-v2`, data);
      return response.data;
    } catch (err: any) {
      // Fallback
      const token = localStorage.getItem(TOKEN_KEY);
      const url = `${API_BASE_URL}/workflow-statuses/${projectId}/workflow-statuses-v2`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
        credentials: 'include',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.message || data?.detail || `Failed to create workflow status (${res.status})`);
      }
      return await res.json();
    }
  },
};
